/**
 * Programme ambassadeur (04/11/2026) — côté administrateur : registre des gains (valider, payer, annuler, exporter),
 * recommandations d'apprenants (transformer, rattacher, classer), ambassadeurs ; et Réglages › Paiements ambassadeurs.
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, CheckCheck, Download, FlaskConical, Landmark, PlugZap, Settings, ShieldCheck } from "lucide-react";
import type { testerConnexionStripe, vueAdminAmbassadeurs, vueReglagesPaiements } from "@/serveur/services/ambassadeurs";
import { api, dateFr, euros, instantFr, ErreurApi, type Json } from "../api";
import { Alerte, Bouton, Carte, Champ, Chargement, EtatVide, Etiquette, Modale, Onglets, Selecteur, TitrePage, ZoneTexte, classesLienBouton, useNotifier } from "../ui/base";
import { LIBELLES_RECO, TableGains, type GainVue } from "./Ambassadeur";

type VueAdmin = Json<Awaited<ReturnType<typeof vueAdminAmbassadeurs>>>;
type Reco = VueAdmin["recommandations"][number];
type VueReglages = Json<Awaited<ReturnType<typeof vueReglagesPaiements>>>;
type ResultatTest = Json<Awaited<ReturnType<typeof testerConnexionStripe>>>;

const STATUTS = [
  { cle: "", libelle: "Tous" },
  { cle: "acquis", libelle: "À valider" },
  { cle: "valide", libelle: "À payer" },
  { cle: "en_attente", libelle: "En attente" },
  { cle: "paye", libelle: "Payés" },
  { cle: "annule", libelle: "Annulés" },
] as const;

export function AdminAmbassadeurs() {
  const [onglet, setOnglet] = useState<"gains" | "recommandations" | "ambassadeurs">("gains");
  const [statut, setStatut] = useState<string>("acquis");
  const [nature, setNature] = useState("");
  const [ambassadeurId, setAmbassadeurId] = useState("");
  const filtres = new URLSearchParams({ ...(statut ? { statut } : {}), ...(nature ? { nature } : {}), ...(ambassadeurId ? { ambassadeur_id: ambassadeurId } : {}) }).toString();
  const vue = useQuery({ queryKey: ["admin-ambassadeurs", filtres], queryFn: () => api.get<VueAdmin>(`/admin/ambassadeurs${filtres ? `?${filtres}` : ""}`) });
  const [paiement, setPaiement] = useState<GainVue | null>(null);
  const [annulation, setAnnulation] = useState<GainVue | null>(null);
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const apres = (message: string) => async () => {
    await requetes.invalidateQueries({ queryKey: ["admin-ambassadeurs"] });
    notifier("succes", message);
  };
  const valider = useMutation({ mutationFn: (id: string) => api.post(`/admin/ambassadeurs/gains/${id}/valider`), onSuccess: apres("Gain validé."), onError: (e) => notifier("danger", e.message) });
  const payerStripe = useMutation({ mutationFn: (id: string) => api.post(`/admin/ambassadeurs/gains/${id}/payer-stripe`), onSuccess: apres("Transfert Stripe créé : gain payé."), onError: (e) => notifier("danger", e.message) });

  if (vue.isPending) return <Chargement />;
  if (vue.error) return <Alerte ton="danger">{vue.error.message}</Alerte>;
  const v = vue.data;
  const t = v.totaux;
  return (
    <>
      <TitrePage
        titre="Programme ambassadeur"
        soustitre="Parrainages de formateurs (prime fixe) et recommandations d'apprenants (pourcentage du montant HT). Aucun gain n'est payé sans votre validation. Les dossiers financés par le CPF n'ouvrent droit à aucun gain."
        actions={
          <>
            <a className={classesLienBouton("secondaire")} href={`/api/admin/ambassadeurs/export${filtres ? `?${filtres}` : ""}`} download>
              <Download className="size-4" aria-hidden /> Exporter (CSV)
            </a>
            <Link to="/admin/reglages/paiements-ambassadeurs" className={classesLienBouton("secondaire")}>
              <Settings className="size-4" aria-hidden /> Réglages › Paiements
            </Link>
          </>
        }
      />
      <div className="mb-4 flex flex-wrap gap-2 text-[13px]">
        <Etiquette ton="attente">À valider : {t.acquis.nombre} · {euros(t.acquis.montant)}</Etiquette>
        <Etiquette ton="accent">À payer : {t.valide.nombre} · {euros(t.valide.montant)}</Etiquette>
        <Etiquette>Payés : {t.paye.nombre} · {euros(t.paye.montant)}</Etiquette>
        <Etiquette>Moyen : {v.moyen === "stripe" ? `Stripe Connect${v.stripe_mode === "test" ? " (mode test)" : ""}` : "virement manuel"}</Etiquette>
      </div>
      <Onglets
        actif={onglet}
        choisir={setOnglet}
        onglets={[
          { cle: "gains", libelle: "Gains" },
          { cle: "recommandations", libelle: "Recommandations", compteur: String(v.recommandations.filter((r) => r.statut === "nouvelle").length || "") || undefined },
          { cle: "ambassadeurs", libelle: "Ambassadeurs" },
        ]}
      />
      {onglet === "gains" && (
        <>
          <div className="mb-3 flex flex-wrap items-end gap-3">
            <div className="flex flex-wrap gap-1" role="group" aria-label="Statut">
              {STATUTS.map((s) => (
                <Bouton key={s.cle} taille="sm" variante={statut === s.cle ? "primaire" : "discret"} aria-pressed={statut === s.cle} onClick={() => setStatut(s.cle)}>
                  {s.libelle}
                </Bouton>
              ))}
            </div>
            <Selecteur libelle="Nature" value={nature} onChange={(e) => setNature(e.target.value)}>
              <option value="">Toutes</option>
              <option value="parrainage">Parrainage</option>
              <option value="recommandation">Recommandation</option>
            </Selecteur>
            <Selecteur libelle="Ambassadeur" value={ambassadeurId} onChange={(e) => setAmbassadeurId(e.target.value)}>
              <option value="">Tous</option>
              {v.ambassadeurs.map((a) => <option key={a.formateur_id} value={a.formateur_id}>{a.nom}</option>)}
            </Selecteur>
          </div>
          {v.gains.length === 0 ? (
            <EtatVide titre="Aucun gain pour ce filtre">Les gains apparaissent dès qu'un filleul s'inscrit ou qu'un dossier issu d'une recommandation est ouvert.</EtatVide>
          ) : (
            <TableGains
              gains={v.gains}
              actions={(g) => (
                <div className="flex justify-end gap-1">
                  {g.statut === "acquis" && (
                    <Bouton taille="sm" variante="primaire" icone={<CheckCheck className="size-3.5" aria-hidden />} enCours={valider.isPending && valider.variables === g.id} onClick={() => valider.mutate(g.id)}>
                      Valider
                    </Bouton>
                  )}
                  {g.statut === "valide" && (
                    <>
                      {v.stripe_pret && (
                        <Bouton taille="sm" variante="signal" icone={<PlugZap className="size-3.5" aria-hidden />} enCours={payerStripe.isPending && payerStripe.variables === g.id} onClick={() => { if (confirm(`Payer ${euros(g.montant_centimes)} par Stripe${v.stripe_mode === "test" ? " (mode test)" : ""} ?`)) payerStripe.mutate(g.id); }}>
                          Payer par Stripe
                        </Bouton>
                      )}
                      <Bouton taille="sm" icone={<Landmark className="size-3.5" aria-hidden />} onClick={() => setPaiement(g)}>
                        Virement fait
                      </Bouton>
                    </>
                  )}
                  {(g.statut === "en_attente" || g.statut === "acquis" || g.statut === "valide") && (
                    <Bouton taille="sm" variante="discret" icone={<Ban className="size-3.5" aria-hidden />} onClick={() => setAnnulation(g)}>
                      Annuler
                    </Bouton>
                  )}
                </div>
              )}
            />
          )}
        </>
      )}
      {onglet === "recommandations" && <Recommandations v={v} />}
      {onglet === "ambassadeurs" && (
        <div className="overflow-x-auto rounded-lg border border-trait bg-carte">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="bg-papier-2 text-left text-[12.5px] text-encre-3">
              <tr><th className="px-3 py-2 font-medium">Formateur</th><th className="px-3 py-2 font-medium">Code</th><th className="px-3 py-2 font-medium">Programme</th><th className="px-3 py-2 font-medium">Parrain</th>{v.stripe_disponible && <th className="px-3 py-2 font-medium">Stripe</th>}</tr>
            </thead>
            <tbody className="divide-y divide-trait">
              {v.ambassadeurs.map((a) => (
                <tr key={a.formateur_id}>
                  <td className="px-3 py-2.5 font-medium">{a.nom}</td>
                  <td className="chiffres px-3 py-2.5">{a.code}</td>
                  <td className="px-3 py-2.5">{a.actif ? <Etiquette ton="accent">Actif</Etiquette> : <Etiquette>Avant 1re session facturée</Etiquette>}</td>
                  <td className="px-3 py-2.5 text-encre-2">{a.parrain || "—"}</td>
                  {v.stripe_disponible && <td className="px-3 py-2.5 text-encre-2">{a.stripe === "actif" ? "Compte actif" : a.stripe === "a_terminer" ? "Inscription à terminer" : "—"}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {paiement && <ModaleVirement g={paiement} fermer={() => setPaiement(null)} />}
      {annulation && <ModaleAnnulation g={annulation} fermer={() => setAnnulation(null)} />}
    </>
  );
}

function ModaleVirement({ g, fermer }: { g: GainVue; fermer: () => void }) {
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const [v, setV] = useState({ date: new Date().toISOString().slice(0, 10), reference: "" });
  const m = useMutation({
    mutationFn: () => api.post(`/admin/ambassadeurs/gains/${g.id}/payer`, v),
    onSuccess: async () => {
      await requetes.invalidateQueries({ queryKey: ["admin-ambassadeurs"] });
      notifier("succes", "Gain marqué payé.");
      fermer();
    },
  });
  const err = m.error instanceof ErreurApi ? (m.error.details?.champs ?? {}) : {};
  return (
    <Modale ouverte fermer={fermer} titre={`Virement de ${euros(g.montant_centimes)}`}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
        <p className="text-sm text-encre-2">Faites le virement depuis votre banque, puis reportez ici sa date et sa référence : le gain passera à « Payé », définitivement.</p>
        <Champ libelle="Date du virement" type="date" required value={v.date} erreur={err.date} onChange={(e) => setV({ ...v, date: e.target.value })} />
        <Champ libelle="Référence du virement" required placeholder="Ex. : VIR-2026-11-04-AMB-01" value={v.reference} erreur={err.reference} onChange={(e) => setV({ ...v, reference: e.target.value })} />
        {m.error && Object.keys(err).length === 0 && <Alerte ton="danger">{m.error.message}</Alerte>}
        <div className="flex justify-end gap-2"><Bouton variante="discret" onClick={fermer}>Annuler</Bouton><Bouton type="submit" variante="primaire" enCours={m.isPending}>Marquer payé</Bouton></div>
      </form>
    </Modale>
  );
}

function ModaleAnnulation({ g, fermer }: { g: GainVue; fermer: () => void }) {
  const requetes = useQueryClient();
  const [motif, setMotif] = useState("");
  const m = useMutation({ mutationFn: () => api.post(`/admin/ambassadeurs/gains/${g.id}/annuler`, { motif }), onSuccess: async () => { await requetes.invalidateQueries({ queryKey: ["admin-ambassadeurs"] }); fermer(); } });
  return (
    <Modale ouverte fermer={fermer} titre="Annuler ce gain">
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
        <ZoneTexte libelle="Motif (visible par l'ambassadeur)" required rows={3} value={motif} onChange={(e) => setMotif(e.target.value)} />
        {m.error && <Alerte ton="danger">{m.error.message}</Alerte>}
        <div className="flex justify-end gap-2"><Bouton variante="discret" onClick={fermer}>Retour</Bouton><Bouton type="submit" variante="danger" enCours={m.isPending}>Annuler le gain</Bouton></div>
      </form>
    </Modale>
  );
}

function Recommandations({ v }: { v: VueAdmin }) {
  const [choix, setChoix] = useState<Record<string, { formateur_id?: string; dossier_id?: string }>>({});
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const action = useMutation({
    mutationFn: ({ id, quoi, corps }: { id: string; quoi: string; corps?: unknown }) => api.post(`/admin/ambassadeurs/recommandations/${id}/${quoi}`, corps ?? {}),
    onSuccess: async () => {
      await requetes.invalidateQueries({ queryKey: ["admin-ambassadeurs"] });
      notifier("succes", "Recommandation mise à jour.");
    },
    onError: (e) => notifier("danger", e.message),
  });
  if (v.recommandations.length === 0) return <EtatVide titre="Aucune recommandation">Les demandes envoyées depuis les formulaires de recommandation des ambassadeurs arrivent ici.</EtatVide>;
  return (
    <ul className="space-y-3">
      {v.recommandations.map((r: Reco) => (
        <li key={r.id}>
          <Carte className="p-4">
            <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-encre-3">
              <span className="chiffres">{r.numero}</span>
              <Etiquette ton={r.statut === "nouvelle" ? "attente" : r.statut === "convertie" ? "accent" : r.statut === "sans_suite" ? "danger" : "neutre"}>{LIBELLES_RECO[r.statut]}</Etiquette>
              <span>reçue le {instantFr(r.cree_le)} · ambassadeur : {r.ambassadeur}</span>
              {r.dossier && <span>· dossier {r.dossier}</span>}
            </div>
            <h3 className="mt-1.5 text-[15px] font-semibold">{`${r.prenom} ${r.nom}`.trim()}</h3>
            {!r.anonymise && <p className="text-sm text-encre-2">{r.email}{r.telephone && ` · ${r.telephone}`}</p>}
            {r.besoin && <p className="mt-1 text-sm whitespace-pre-line">{r.besoin}</p>}
            <p className="mt-1 text-[12px] text-encre-3">Consentement recueilli le {dateFr(r.consentement_le)}{r.formateur && ` · confiée à ${r.formateur}`}</p>
            {r.motif && <Alerte ton="attention">{r.motif}</Alerte>}
            {(r.statut === "nouvelle" || r.statut === "transformee") && (
              <div className="mt-3 flex flex-wrap items-end gap-2">
                {r.statut === "nouvelle" && (
                  <>
                    <Selecteur libelle="Confier à" value={choix[r.id]?.formateur_id ?? r.ambassadeur_id} onChange={(e) => setChoix({ ...choix, [r.id]: { ...choix[r.id], formateur_id: e.target.value } })}>
                      {v.formateurs.map((f) => <option key={f.id} value={f.id}>{f.nom}</option>)}
                    </Selecteur>
                    <Bouton variante="primaire" taille="sm" onClick={() => action.mutate({ id: r.id, quoi: "transformer", corps: { formateur_id: choix[r.id]?.formateur_id ?? r.ambassadeur_id } })}>
                      Transformer en dossier
                    </Bouton>
                  </>
                )}
                <Selecteur libelle="Ou rattacher à un dossier existant" value={choix[r.id]?.dossier_id ?? ""} onChange={(e) => setChoix({ ...choix, [r.id]: { ...choix[r.id], dossier_id: e.target.value } })}>
                  <option value="">—</option>
                  {v.dossiers.map((d) => <option key={d.id} value={d.id}>{d.libelle} ({d.formateur})</option>)}
                </Selecteur>
                <Bouton taille="sm" disabled={!choix[r.id]?.dossier_id} onClick={() => action.mutate({ id: r.id, quoi: "rattacher", corps: { dossier_id: choix[r.id]?.dossier_id } })}>
                  Rattacher
                </Bouton>
                <Bouton taille="sm" variante="discret" onClick={() => { const motif = prompt("Motif du classement sans suite :"); if (motif) action.mutate({ id: r.id, quoi: "sans-suite", corps: { motif } }); }}>
                  Sans suite
                </Bouton>
              </div>
            )}
            {!r.anonymise && r.statut !== "nouvelle" && r.statut !== "transformee" && (
              <div className="mt-2">
                <Bouton taille="sm" variante="discret" onClick={() => { if (confirm("Effacer les coordonnées de cette personne (RGPD) ?")) action.mutate({ id: r.id, quoi: "anonymiser" }); }}>
                  Anonymiser
                </Bouton>
              </div>
            )}
          </Carte>
        </li>
      ))}
    </ul>
  );
}

/** Réglages › Paiements ambassadeurs : moyen de paiement, clés Stripe (champs masqués), paramètres du programme. */
export function ReglagesPaiementsAmbassadeurs() {
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const vue = useQuery({ queryKey: ["reglages-ambassadeurs"], queryFn: () => api.get<VueReglages>("/admin/reglages/paiements-ambassadeurs") });
  const [cles, setCles] = useState({ stripe_cle: "", stripe_webhook: "", stripe_webhook_connect: "" });
  const [test, setTest] = useState<ResultatTest | null>(null);
  const enregistrer = useMutation({
    mutationFn: (corps: unknown) => api.patch<VueReglages>("/admin/reglages/paiements-ambassadeurs", corps),
    onSuccess: async () => {
      setCles({ stripe_cle: "", stripe_webhook: "", stripe_webhook_connect: "" });
      await requetes.invalidateQueries({ queryKey: ["reglages-ambassadeurs"] });
      notifier("succes", "Réglages enregistrés.");
    },
    onError: (e) => notifier("danger", e.message),
  });
  const tester = useMutation({ mutationFn: () => api.post<ResultatTest>("/admin/reglages/paiements-ambassadeurs/tester"), onSuccess: setTest, onError: (e) => notifier("danger", e.message) });
  if (vue.isPending) return <Chargement />;
  if (vue.error) return <Alerte ton="danger">{vue.error.message}</Alerte>;
  const v = vue.data;
  const p = v.parametres;
  return (
    <>
      <TitrePage titre="Paiements ambassadeurs" soustitre="Comment les gains validés des ambassadeurs sont réglés, et les règles du programme." actions={<Link to="/admin/ambassadeurs" className={classesLienBouton("secondaire")}>Retour au registre</Link>} />
      <div className="grid gap-4 lg:grid-cols-2">
        <Carte className="space-y-4 p-5">
          <h2 className="text-[15px] font-semibold">Moyen de paiement</h2>
          {!v.stripe && (
            <p className="text-sm text-encre-2">
              <strong>Virement manuel.</strong> Vous faites le virement depuis votre banque, puis vous marquez le gain « payé » avec la date et la référence du virement. L'ambassadeur établit sa facture à partir de son relevé.
            </p>
          )}
          {v.stripe && (["virement", "stripe"] as const).map((m) => (
            <label key={m} className="flex items-start gap-3 text-sm">
              <input type="radio" name="moyen" className="mt-0.5 size-4 accent-[var(--color-accent)]" checked={v.moyen === m} onChange={() => enregistrer.mutate({ moyen: m })} />
              <span>
                <strong>{m === "virement" ? "Virement manuel (par défaut)" : "Stripe Connect (Express)"}</strong>
                <span className="block text-encre-3">{m === "virement" ? "Vous faites le virement depuis votre banque, puis vous marquez le gain « payé » avec la référence du virement." : "L'ambassadeur connecte son compte Stripe ; un clic sur « Payer par Stripe » crée un transfert depuis le solde de votre compte Stripe."}</span>
              </span>
            </label>
          ))}
          <Alerte ton="info">Aucun paiement n'est jamais automatique : chaque gain doit d'abord être validé par un administrateur, puis payé d'un clic.</Alerte>
        </Carte>

        {v.stripe && (
        <Carte className="space-y-4 p-5">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-[15px] font-semibold">Clés Stripe</h2>
            {v.stripe.mode === "test" && <Etiquette ton="attente"><FlaskConical className="mr-1 inline size-3.5" aria-hidden />Mode test : aucun argent réel</Etiquette>}
            {v.stripe.mode === "reel" && <Etiquette ton="danger">Mode réel</Etiquette>}
          </div>
          <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); enregistrer.mutate({ ...(cles.stripe_cle ? { stripe_cle: cles.stripe_cle } : {}), ...(cles.stripe_webhook ? { stripe_webhook: cles.stripe_webhook } : {}), ...(cles.stripe_webhook_connect ? { stripe_webhook_connect: cles.stripe_webhook_connect } : {}) }); }}>
            <Champ libelle="Clé secrète" type="password" autoComplete="off" placeholder={v.stripe.cle_definie ? "•••••••• enregistrée — laisser vide pour la garder" : "sk_test_… puis sk_live_…"} value={cles.stripe_cle} onChange={(e) => setCles({ ...cles, stripe_cle: e.target.value })} aide="Tableau de bord Stripe › Développeurs › Clés API. Jamais la clé publiable « pk_… »." />
            <Champ libelle="Secret du webhook — destination « Votre compte »" type="password" autoComplete="off" placeholder={v.stripe.webhook_defini ? "•••••••• enregistré — laisser vide pour le garder" : "whsec_…"} value={cles.stripe_webhook} onChange={(e) => setCles({ ...cles, stripe_webhook: e.target.value })} />
            <Champ libelle="Secret du webhook — destination « Comptes connectés » (facultatif)" type="password" autoComplete="off" placeholder={v.stripe.webhook_connect_defini ? "•••••••• enregistré — laisser vide pour le garder" : "whsec_…"} value={cles.stripe_webhook_connect} onChange={(e) => setCles({ ...cles, stripe_webhook_connect: e.target.value })} aide="Sert à suivre l'inscription Stripe des ambassadeurs (account.updated). Sans lui, l'état est relu à chaque paiement." />
            <div className="rounded-md bg-papier-2 px-3 py-2 text-[12.5px] text-encre-2">
              Adresse à déclarer chez Stripe (deux destinations, même adresse) :
              <code className="chiffres mt-1 block break-all text-encre">{v.stripe.url_webhook}</code>
              « Votre compte » : {v.stripe.evenements_webhook.join(", ")} · « Comptes connectés » : {v.stripe.evenements_webhook_connect.join(", ")}.
            </div>
            <div className="flex flex-wrap gap-2">
              <Bouton type="submit" variante="primaire" enCours={enregistrer.isPending} disabled={!cles.stripe_cle && !cles.stripe_webhook && !cles.stripe_webhook_connect}>Enregistrer les clés</Bouton>
              <Bouton icone={<ShieldCheck className="size-4" aria-hidden />} enCours={tester.isPending} disabled={!v.stripe.cle_definie} onClick={() => tester.mutate()}>Tester la connexion</Bouton>
              {v.stripe.cle_definie && <Bouton variante="discret" onClick={() => { if (confirm("Effacer les clés Stripe enregistrées ?")) enregistrer.mutate({ moyen: "virement", stripe_cle: "-", stripe_webhook: "-", stripe_webhook_connect: "-" }); }}>Effacer les clés</Bouton>}
            </div>
          </form>
          {test && (test.ok ? <Alerte ton="succes" titre={`Connexion réussie (mode ${test.mode === "test" ? "test" : "réel"})`}>Solde disponible : {euros(test.solde_disponible)}.{!test.webhook_defini && " Pensez à saisir le secret du webhook."}</Alerte> : <Alerte ton="danger" titre="Connexion refusée">{test.erreur}</Alerte>)}
        </Carte>
        )}

        <Carte className="space-y-4 p-5 lg:col-span-2">
          <h2 className="text-[15px] font-semibold">Règles du programme</h2>
          <ParametresProgramme p={p} enregistrer={(x) => enregistrer.mutate({ parametres: x })} enCours={enregistrer.isPending} />
          <Alerte ton="attention" titre="CPF : exclusion totale">Aucun gain ne naît d'un dossier financé par le CPF (démarchage interdit, art. L6323-8-1 du code du travail ; CGU de Mon Compte Formation). Ce point n'est pas réglable.</Alerte>
        </Carte>
      </div>
    </>
  );
}

function ParametresProgramme({ p, enregistrer, enCours }: { p: VueReglages["parametres"]; enregistrer: (p: Partial<VueReglages["parametres"]>) => void; enCours: boolean }) {
  const [v, setV] = useState(p);
  return (
    <form className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" onSubmit={(e) => { e.preventDefault(); enregistrer(v); }}>
      <Champ libelle="Prime de parrainage (€)" type="number" min={0} step="0.01" value={v.prime_parrainage_centimes / 100} onChange={(e) => setV({ ...v, prime_parrainage_centimes: Math.round(Number(e.target.value) * 100) })} />
      <Champ libelle="Commission de recommandation (%)" type="number" min={0} max={20} step="0.1" value={v.taux_recommandation_pourcent} onChange={(e) => setV({ ...v, taux_recommandation_pourcent: Number(e.target.value) })} />
      <Selecteur libelle="La prime de parrainage est acquise…" value={v.declencheur_parrainage} onChange={(e) => setV({ ...v, declencheur_parrainage: e.target.value as typeof v.declencheur_parrainage })}>
        <option value="dossier_valide">au 1er dossier du filleul validé par l'organisme</option>
        <option value="formation_realisee">à la fin de la 1re formation du filleul</option>
      </Selecteur>
      <label className="flex items-start gap-3 text-sm sm:col-span-2 lg:col-span-3">
        <input type="checkbox" className="mt-0.5 size-4 accent-[var(--color-accent)]" checked={v.recommandation_soi_meme} onChange={(e) => setV({ ...v, recommandation_soi_meme: e.target.checked })} />
        <span>Verser la commission de recommandation quand l'ambassadeur forme lui-même l'apprenant recommandé <span className="text-encre-3">(désactivé par défaut)</span></span>
      </label>
      <div className="sm:col-span-2 lg:col-span-3"><Bouton type="submit" variante="primaire" enCours={enCours}>Enregistrer les règles</Bouton></div>
    </form>
  );
}
